import React from "react";
import { formatINR, getDisplayValue } from "./settlementHelpers";
import Styles from "./Settlement.module.css";

const SettlementRow = ({
  ele,
  index,
  role,
  onAmountChange,
  onCheckerAction,
  onCommentChange,
  onResetRow,
  rawDamValue,
  type,
  hideBearerPayer,
}) => {
  const showVerify = role === "MAKER" || role === "CHECKER";
  const chargeName = ele?.name || ele?.chargeName || "—";
  const isFixed = (ele?.type || ele?.chargeType) !== "PER_MW";
  const chargeRate = isFixed
    ? "Fixed"
    : `₹${ele?.value || ele?.chargeValue?.toFixed(3) || 0}/kWh`;

  const getMakerStatus = () => {
    if (["CHECKER", "PAYMENT"].includes(ele?.status)) return "approved";
    if (ele?.status === "RECHECK") return "rejected";
    return "pending";
  };

  const getCheckerStatus = () => {
    if (ele?.status === "PAYMENT") return "approved";
    if (ele?.status === "RECHECK") return "rejected";
    return "pending";
  };

  const isReadOnly = (() => {
    if (role === "MAKER" && ["CHECKER", "PAYMENT"].includes(ele?.status))
      return true;
    if (role === "CHECKER") return true;
    if (
      (role === "CONSUMER" ||
        role === "GENERATOR" ||
        role === "SUPER_ADMIN" ||
        role === "ADMIN") &&
      ["MAKER", "CHECKER", "PAYMENT"].includes(ele?.status)
    )
      return true;
    return false;
  })();

  const isAmountReadOnly =
    isReadOnly || (ele?.isNonEditable && role === "CONSUMER");
  const displayValue = getDisplayValue(ele, role);

  const approveActive =
    role === "MAKER" ? ele?.status === "CHECKER" : ele?.status === "PAYMENT";
  const rejectActive = ele?.status === "RECHECK";

  const canApprove =
    (role === "CHECKER" &&
      ele?.status !== "PAYMENT" &&
      ele?.status !== "RECHECK" &&
      type !== "HISTORY") ||
    (role === "MAKER" && ele?.status !== "PAYMENT" && type !== "HISTORY");

  const canReject =
    role === "CHECKER" &&
    ele?.status !== "RECHECK" &&
    ele?.status !== "PAYMENT" &&
    type !== "HISTORY";

  const rowTint = approveActive
    ? Styles.rowApproved
    : rejectActive
      ? Styles.rowRejected
      : "";

  return (
    <div
      className={`tableDetails display_flex_row ${Styles.rowMainContainerList} ${rowTint}`}
    >
      <div style={{ width: "12%" }} title={chargeName}>
        {chargeName}
      </div>
      <div style={{ width: "9%" }} title={ele?.fromUser}>
        {ele?.fromUser}
      </div>
      <div style={{ width: "9%" }} title={ele?.toUser}>
        {ele?.toUser}
      </div>

      {!hideBearerPayer && (
        <div style={{ width: "6%" }} title={ele?.bearer || ele?.fromUserRole}>
          {ele?.bearer || ele?.fromUserRole}
        </div>
      )}
      {!hideBearerPayer && (
        <div style={{ width: "6%" }} title={ele?.payer || ele?.toUserRole}>
          {ele?.payer || ele?.toUserRole}
        </div>
      )}

      {/* Est. Amount */}
      <div style={{ width: "10%" }}>
        <div className={Styles.amtCell}>
          {ele?.amount != null || ele?.chargeAmount != null ? (
            <span className={Styles.numMono}>
              {formatINR(ele?.amount ?? ele?.chargeAmount) ?? "—"}
            </span>
          ) : (
            <span className={Styles.dash}>—</span>
          )}
          <span
            className={`${Styles.rateSub} ${isFixed ? Styles.rateFixed : ""}`}
          >
            {chargeRate}
          </span>
          {ele?.fromUser === "Consumer" &&
            ele?.toUser === "Generator" &&
            rawDamValue && (
              <span className={Styles.damValue}>{rawDamValue}</span>
            )}
        </div>
      </div>

      {/* Final Amount */}
      <div style={{ width: "10%" }}>
        {isAmountReadOnly ? (
          <div className={Styles.final}>{formatINR(displayValue) ?? "—"}</div>
        ) : (
          <div className={Styles.final}>
            <div className={`${Styles.finbox} ${Styles.finboxEdit}`}>
              <input
                className={Styles.finboxInput}
                type="text"
                value={displayValue || ""}
                onChange={(e) => onAmountChange(e, index)}
                inputMode="decimal"
              />
            </div>
          </div>
        )}
      </div>

      {/* Maker Comment */}
      <div style={{ width: "10%" }}>
        {role === "MAKER" && !isReadOnly ? (
          <div className={`${Styles.finbox} ${Styles.finboxEdit}`}>
            <input
              placeholder={
                ele?.isMakerAmountEdited ? "Required…" : "Add comment…"
              }
              value={ele?.makerComment || ""}
              onChange={(e) =>
                onCommentChange(index, "makerComment", e.target.value)
              }
              data-row-index={index}
              data-comment-type="maker"
              type="text"
            />
          </div>
        ) : (
          <>{ele?.makerComment || "—"}</>
        )}
      </div>

      {/* Checker Comment */}
      <div style={{ width: "10%" }}>
        {role === "CHECKER" && type !== "HISTORY" ? (
          <div className={`${Styles.finbox} ${Styles.finboxEdit}`}>
            <input
              className={`${Styles.commentInput} ${!ele?.checkerComment?.trim() ? Styles.req : ""}`}
              placeholder="Required…"
              value={ele?.checkerComment || ""}
              onChange={(e) =>
                onCommentChange(index, "checkerComment", e.target.value)
              }
              data-row-index={index}
              data-comment-type="checker"
              type="text"
            />
          </div>
        ) : (
          <>{ele?.checkerComment || "—"}</>
        )}
      </div>

      {/* Verify actions */}
      {showVerify && (
        <div style={{ width: "6%" }}>
          <div className={Styles.verifyBtns}>
            {role === "CHECKER" && (
              <button
                className={`${Styles.verifyBtn} ${Styles.rejectBtn} ${rejectActive ? Styles.active : ""} ${approveActive ? Styles.faded : ""}`}
                title="Reject this charge"
                onClick={() =>
                  canReject && onCheckerAction("reject", ele, index)
                }
                disabled={!canReject}
              >
                ✕
              </button>
            )}
            <button
              className={`${Styles.verifyBtn} ${Styles.approveBtn} ${approveActive ? Styles.active : ""} ${rejectActive ? Styles.faded : ""}`}
              title="Approve this charge"
              onClick={() =>
                canApprove && onCheckerAction("accept", ele, index)
              }
              disabled={!canApprove}
            >
              ✓
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettlementRow;
